import {
    Button,
    Flex,
    NumberInput,
    Select,
    SelectItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    Text,
    TextInput,
    Title,
} from '@tremor/react'
import { useEffect, useRef, useState } from 'react'
import { ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import {
    useApiV1KeypairsList,
    useApiV1OdooCreateCreate,
    useApiV1OdooDelete,
    useApiV1OdoosList,
} from '../../api/api.gen'
import Modal from '../../components/Layout/modal'
import { toErrorString, toErrorText } from '../../utilities/error'
import Confirm from '../../components/Layout/confirm'
import { ModelHttpsMode } from '../../api/api'
import Spinner from '../../components/Spinner'

export default function OdooPage() {
    const [deleteID, setDeleteID] = useState<number>()
    const [showModal, setShowModal] = useState(false)
    const [showIPModal, setShowIPModal] = useState(false)
    const [vmID, setVMID] = useState<number>()

    const [key, setKey] = useState('')
    const [keyFilename, setKeyFilename] = useState('')
    const [certificate, setCertificate] = useState('')
    const [certificateFilename, setCertificateFilename] = useState('')

    const [httpsMode, setHttpsMode] = useState<ModelHttpsMode>(
        ModelHttpsMode.HttpsModeDISABLED
    )

    const [keyPairName, setKeyPairName] = useState('')
    const [clusterName, setClusterName] = useState<string>('')
    const [domainName, setDomainName] = useState<string>('')
    const [workers, setWorkers] = useState<number>(1)
    const [volumeSize, setVolumeSize] = useState<number>(8)
    const [dedicated, setDedicated] = useState<boolean>(false)
    const [staging, setStaging] = useState<number>(0)
    const httpsKeyRef = useRef<HTMLInputElement>(null)
    const httpsCertificateRef = useRef<HTMLInputElement>(null)
    const { response: keypairList } = useApiV1KeypairsList()
    const {
        response: createResponse,
        isLoading: createLoading,
        isExecuted: createExecuted,
        sendNowWithParams: create,
        error: createError,
    } = useApiV1OdooCreateCreate({}, {}, false)

    const {
        isLoading: deleteLoading,
        isExecuted: deleteExecuted,
        sendNowWithParams: deleteVM,
        error: deleteError,
    } = useApiV1OdooDelete({ id: 0 }, {}, false)

    const {
        isLoading: vmListLoading,
        isExecuted: vmListExecuted,
        response: vmList,
        sendNow: refresh,
    } = useApiV1OdoosList()

    const targetVMIP = vmList?.items
        ?.filter((v) => v.id === vmID)
        .map((v) => v.ipAddress)
        .at(0)

    useEffect(() => {
        if (
            createExecuted &&
            !createLoading &&
            toErrorString(createError).length === 0
        ) {
            setShowModal(false)
            if (httpsMode === ModelHttpsMode.HttpsModeLETSENCRYPT) {
                setVMID(createResponse?.id)
                setShowIPModal(true)
            }
            refresh()
        }
    }, [createLoading])

    useEffect(() => {
        if (
            deleteExecuted &&
            !deleteLoading &&
            toErrorString(deleteError).length === 0
        ) {
            setDeleteID(undefined)
            refresh()
        }
    }, [deleteLoading])

    useEffect(() => {
        if (
            vmID !== undefined &&
            showIPModal &&
            (targetVMIP || '').length === 0
        ) {
            setTimeout(() => refresh(), 1000)
        }
    }, [vmList])

    const readFile = (
        event: React.ChangeEvent<HTMLInputElement>,
        load: (text: string) => void,
        setFilename: (filename: string) => void
    ) => {
        if (event.target.files !== null) {
            for (let i = 0; i < event.target.files.length; i += 1) {
                const file = event.target.files.item(i)
                if (file) {
                    const reader = new FileReader()

                    // Callback when the file is successfully read
                    reader.onload = (e) => {
                        const fileContent = e.target?.result
                        if (fileContent !== undefined && fileContent !== null) {
                            load(fileContent as string)
                            setFilename(file.name)
                        }
                    }

                    // Callback in case of error while reading
                    reader.onerror = (e) => {
                        // TODO - error
                    }

                    // Read the file as text
                    reader.readAsText(file)
                }
            }
        }
    }

    const handleHttpsKeyChanged = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        readFile(event, setKey, setKeyFilename)
    }

    const handleHttpsCertificateChanged = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        readFile(event, setCertificate, setCertificateFilename)
    }

    return (
        <Flex
            className="p-4 bg-gray-50 w-auto min-w-[1000px] max-w-fit mt-4 rounded-md"
            flexDirection="col"
            justifyContent="start"
            alignItems="start"
        >
            <Flex flexDirection="row" justifyContent="between">
                <Title className="text-teal-600">Odoo instances</Title>
                <Flex className="w-fit space-x-2">
                    <Button
                        icon={ArrowPathIcon}
                        variant="secondary"
                        color="teal"
                        disabled={vmListExecuted && vmListLoading}
                        onClick={() => refresh()}
                    />
                    <Button
                        variant="secondary"
                        color="teal"
                        onClick={() => setShowModal(true)}
                    >
                        Create
                    </Button>
                </Flex>
            </Flex>
            <Table className="w-full">
                <TableHead>
                    <TableRow>
                        <TableHeaderCell>Name</TableHeaderCell>
                        <TableHeaderCell>Domain Name</TableHeaderCell>
                        <TableHeaderCell>SSH Key</TableHeaderCell>
                        <TableHeaderCell>HTTPS</TableHeaderCell>
                        <TableHeaderCell>Volume Size</TableHeaderCell>
                        <TableHeaderCell># of Workers</TableHeaderCell>
                        <TableHeaderCell>Status</TableHeaderCell>
                        <TableHeaderCell>IP Address</TableHeaderCell>
                        <TableHeaderCell>SSH Command</TableHeaderCell>
                        <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {vmList?.items
                        ?.sort((a, b) => (b.id || 0) - (a.id || 0))
                        .map((v) => (
                            <TableRow>
                                <TableCell>{v.name}</TableCell>
                                <TableCell>{v.domain_name}</TableCell>
                                <TableCell>{v.keyPairName}</TableCell>
                                <TableCell>{v.https?.httpsOptions}</TableCell>
                                <TableCell>{v.volumeSize} GB</TableCell>
                                <TableCell>{v.workerCount}</TableCell>
                                <TableCell>{v.status}</TableCell>
                                <TableCell>{v.ipAddress}</TableCell>
                                <TableCell>
                                    {v.status !== 'deleted' && (
                                        <>ssh ubuntu@{v.ipAddress}</>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {v.status !== 'deleted' && (
                                        <TrashIcon
                                            onClick={() => setDeleteID(v.id)}
                                            className="w-4 h-4 text-teal-500 cursor-pointer"
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    {(vmList?.items?.length || 0) === 0 && (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Text className="w-full text-gray-400 text-center font-normal">
                                    {vmListExecuted && vmListLoading
                                        ? 'Loading'
                                        : 'Empty list'}
                                </Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <Confirm
                title={`Are you sure you want to delete ${vmList?.items
                    ?.filter((v) => v.id === deleteID)
                    .map((v) => v.name)}?`}
                show={deleteID !== undefined}
                loading={deleteExecuted && deleteLoading}
                error={deleteError}
                onCanceled={() => setDeleteID(undefined)}
                onConfirmed={() => deleteVM({ id: deleteID || 0 }, {})}
            />

            <Modal
                title="Set IP Address"
                show={showIPModal}
                onCloseClicked={() => 0}
                className="min-w-96"
                position="center"
            >
                <Flex
                    flexDirection="col"
                    alignItems="center"
                    className="space-y-2 pt-4"
                >
                    <Text className="text-black">
                        Please set this IP address on your dns server.
                    </Text>
                    {(targetVMIP || '').length === 0 ? (
                        <Spinner />
                    ) : (
                        <Text className="text-black">{targetVMIP || ''}</Text>
                    )}
                    <Text className="text-black">
                        NOTE: this step is required for a successful
                        installation of Odoo instance
                    </Text>
                    <Button
                        variant="secondary"
                        color="teal"
                        disabled={(targetVMIP || '').length === 0}
                        loading={(targetVMIP || '').length === 0}
                        onClick={() => setShowIPModal(false)}
                    >
                        {(targetVMIP || '').length === 0
                            ? 'Please wait, it might take a few minutes'
                            : 'I added this DNS record'}
                    </Button>
                </Flex>
            </Modal>

            <Modal
                title="Create a new Odoo instance"
                show={showModal}
                onCloseClicked={() => setShowModal(false)}
                className="min-w-96"
            >
                <Text className="mt-4 mb-1">Instance name:</Text>
                <TextInput
                    value={clusterName}
                    onValueChange={setClusterName}
                    error={clusterName.length === 0}
                    errorMessage="Instance name is required"
                />

                <Text className="mt-4 mb-1">Domain name:</Text>
                <TextInput
                    value={domainName}
                    onValueChange={setDomainName}
                    error={domainName.length === 0}
                    errorMessage="Domain name is required"
                />

                <Text className="mt-4 mb-1">SSH Key:</Text>
                <Select
                    value={keyPairName}
                    onValueChange={setKeyPairName}
                    error={keyPairName.length === 0}
                    errorMessage="SSH Key is required"
                >
                    {keypairList?.items?.map((k) => {
                        return (
                            <SelectItem value={k.name || ''}>
                                {k.name}
                            </SelectItem>
                        )
                    })}
                </Select>
                <Flex flexDirection="row" justifyContent="end">
                    <Link
                        to="/sshkey#import"
                        className="underline text-blue-600"
                    >
                        Import a ssh key
                    </Link>
                </Flex>

                <Text className="mt-4 mb-1">HTTPS:</Text>
                <Select
                    value={httpsMode}
                    onValueChange={(v) => setHttpsMode(v as ModelHttpsMode)}
                    error={httpsMode.length === 0}
                    errorMessage="Https mode is required"
                >
                    <SelectItem value={ModelHttpsMode.HttpsModeDISABLED}>
                        Disabled (HTTP only)
                    </SelectItem>
                    <SelectItem value={ModelHttpsMode.HttpsModeLETSENCRYPT}>
                        HTTPS (Let&apos;s Encrypt)
                    </SelectItem>
                    <SelectItem
                        value={ModelHttpsMode.HttpsModeCUSTOMERCERTIFICATE}
                    >
                        HTTPS (Your certificate)
                    </SelectItem>
                </Select>
                {httpsMode === ModelHttpsMode.HttpsModeCUSTOMERCERTIFICATE && (
                    <Flex
                        flexDirection="col"
                        alignItems="start"
                        className="mt-1"
                    >
                        <input
                            ref={httpsKeyRef}
                            type="file"
                            className="hidden"
                            onChange={handleHttpsKeyChanged}
                        />
                        <Flex
                            onClick={() => {
                                httpsKeyRef?.current?.click()
                            }}
                            className="cursor-pointer"
                        >
                            <Text className="underline text-blue-600">
                                Import key
                            </Text>
                            {httpsKeyRef?.current?.value !== undefined ? (
                                <Text>{keyFilename}</Text>
                            ) : (
                                <Text className="text-red-600">Required</Text>
                            )}
                        </Flex>
                        <input
                            ref={httpsCertificateRef}
                            type="file"
                            className="hidden"
                            onChange={handleHttpsCertificateChanged}
                        />
                        <Flex
                            onClick={() => {
                                httpsCertificateRef?.current?.click()
                            }}
                            className="cursor-pointer"
                        >
                            <Text className="underline text-blue-600">
                                Import certificate
                            </Text>
                            {httpsCertificateRef?.current?.value !==
                            undefined ? (
                                <Text>{certificateFilename}</Text>
                            ) : (
                                <Text className="text-red-600">Required</Text>
                            )}
                        </Flex>
                    </Flex>
                )}

                <Text className="mt-4 mb-1"># of workers:</Text>
                <NumberInput
                    value={workers}
                    onValueChange={setWorkers}
                    min={1}
                    max={64}
                />

                <Text className="mt-4 mb-1">Volume Size (GB):</Text>
                <NumberInput
                    min={8}
                    max={512}
                    value={volumeSize}
                    onValueChange={setVolumeSize}
                    error={volumeSize < 8 || volumeSize > 512}
                    errorMessage="Volume size must be within 8-512 range"
                />

                {/* <Text className="mt-4 mb-1">Hosting type:</Text>
                <Select
                    value={dedicated ? 'dedicated' : 'shared'}
                    onValueChange={(v) => {
                        setDedicated(v === 'dedicated')
                    }}
                >
                    <SelectItem value="shared">Shared</SelectItem>
                    <SelectItem value="dedicated">Dedicated</SelectItem>
                </Select> */}

                {/* <Text className="mt-4 mb-1">Staging env:</Text>
                <NumberInput
                    min={0}
                    max={20}
                    value={staging}
                    onValueChange={setStaging}
                    error={staging < 0 || staging > 20}
                    errorMessage="Staging environment must be within 0-20 range"
                /> */}

                <div className="mt-8">{toErrorText(createError)}</div>
                <Button
                    className=""
                    color="teal"
                    loading={createLoading && createExecuted}
                    disabled={createLoading && createExecuted}
                    onClick={() => {
                        create(
                            {
                                name: clusterName,
                                domainName,
                                dedicated,
                                stagingEnvCount: staging,
                                volumeSize,
                                workerCount: workers,
                                keyPairName,
                                https: {
                                    key,
                                    certificate,
                                    httpsOptions: httpsMode,
                                },
                            },
                            {}
                        )
                    }}
                >
                    Create
                </Button>
            </Modal>
        </Flex>
    )
}
